import React, { useState, useEffect, useMemo } from 'react';
import '../styles/modal.css';

const TimerManagementPopup = ({ 
    isOpen, 
    onClose, 
    activeTimers, 
    currentStepIndex = null,
    onAddTimer,
    onRemoveTimer,
    onPauseTimer,
    onResumeTimer,
    onMuteTimer,
    onUnmuteTimer,
    formatTime
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x: 96, y: 96 });
    const [newTimerTitle, setNewTimerTitle] = useState('');
    const [newTimerMinutes, setNewTimerMinutes] = useState(5);
    const [showAddForm, setShowAddForm] = useState(false);

    // Handle dragging
    const handleMouseDown = (e) => {
        if (e.target.closest('.timer-controls') || e.target.closest('.add-timer-form')) {
            return; // Don't drag if clicking on controls
        }
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    useEffect(() => {
        if (isDragging) {
            const handleMouseMove = (e) => {
                setPosition({
                    x: e.clientX - dragOffset.x,
                    y: e.clientY - dragOffset.y
                });
            };

            const handleMouseUp = () => {
                setIsDragging(false);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    // Keyboard navigation - ESC to close
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleAddTimer = () => {
        if (newTimerTitle.trim() && newTimerMinutes > 0) {
            onAddTimer(newTimerTitle.trim(), newTimerMinutes * 60);
            setNewTimerTitle('');
            setNewTimerMinutes(5);
            setShowAddForm(false);
        }
    };

    // Ensure activeTimers is always an array and memoize it
    const safeActiveTimers = useMemo(() => {
        return Array.isArray(activeTimers) ? activeTimers : [];
    }, [activeTimers]);

    if (!isOpen) return null;

    return (
        <div 
            className="fixed z-50 bg-neutral-0 w-96 shadow-popup border border-black"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Title Bar */}
            <div className="title-bar bg-neutral-0 text-neutral-900 px-4 py-2 flex justify-between items-center cursor-grab active:cursor-grabbing select-none hairline-b">
                <span className="font-mono uppercase text-sm tracking-wider truncate">
                    Timers
                </span>
                <button 
                    onClick={onClose}
                    className="text-neutral-900 text-base flex-shrink-0 border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:text-neutral-500 transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Content Area */}
            <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {/* Active Timers List */}
                {safeActiveTimers.length === 0 ? (
                    <div className="text-center text-neutral-500 py-8">
                        <p className="font-mono text-sm">No timers</p>
                        <p className="font-mono text-xs mt-1">Add a timer to get started</p>
                    </div>
                ) : (
                    // Sort timers: current step first, then by step index (most recent first), then active before inactive
                    [...safeActiveTimers].sort((a, b) => {
                        const aStep = a.stepIndex !== null ? a.stepIndex : -1;
                        const bStep = b.stepIndex !== null ? b.stepIndex : -1;
                        const currentStep = currentStepIndex !== null ? currentStepIndex : -1;
                        
                        // Current step timers first
                        if (aStep === currentStep && bStep !== currentStep) return -1;
                        if (aStep !== currentStep && bStep === currentStep) return 1;
                        
                        // Within same step group, sort by step index (higher = more recent)
                        if (aStep !== bStep) return bStep - aStep;
                        
                        // Within same step, active timers before inactive
                        if (a.kind === 'active' && b.kind === 'inactive') return -1;
                        if (a.kind === 'inactive' && b.kind === 'active') return 1;
                        
                        // Then by active status (running timers first)
                        if (a.isActive && !b.isActive) return -1;
                        if (!a.isActive && b.isActive) return 1;
                        
                        // Finally by remaining time (lowest first)
                        return a.remaining - b.remaining;
                    }).map((timer) => (
                        <div 
                            key={timer.id} 
                            className={`bg-neutral-50 hairline p-3 timer-controls ${!timer.isActive ? 'opacity-60' : ''}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex-1">
                                    <div className="font-mono text-sm font-medium text-neutral-900 flex items-center gap-2">
                                        {timer.title}
                                        {!timer.isActive && timer.remaining > 0 && (
                                            <span className="font-mono text-xs text-neutral-500">(Paused)</span>
                                        )}
                                        {timer.remaining <= 0 && (
                                            <span className="font-mono text-xs text-red-600">(Finished)</span>
                                        )}
                                    </div>
                                    {timer.stepIndex !== null && (
                                        <div className="font-mono text-xs text-neutral-500 mt-1">
                                            Step {timer.stepIndex + 1}
                                        </div>
                                    )}
                                    {timer.kind && (
                                        <div className="font-mono text-xs text-neutral-500 mt-1">
                                            {timer.kind === 'active' ? 'Active' : 'Inactive'} timer
                                        </div>
                                    )}
                                </div>
                                <div className={`font-mono text-lg ${timer.remaining <= 0 ? 'text-red-600' : 'text-neutral-900'}`}>
                                    {formatTime(timer.remaining)}
                                </div>
                            </div>
                            
                            <div className="flex gap-2 mt-2">
                                {timer.isActive ? (
                                    <button
                                        onClick={() => onPauseTimer(timer.id)}
                                        className="px-2 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider"
                                    >
                                        Pause
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => onResumeTimer(timer.id)}
                                        className="px-2 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider"
                                    >
                                        Resume
                                    </button>
                                )}
                                
                                {timer.isMuted ? (
                                    <button
                                        onClick={() => onUnmuteTimer(timer.id)}
                                        className="px-2 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider"
                                        title="Unmute timer"
                                    >
                                        🔊
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => onMuteTimer(timer.id)}
                                        className="px-2 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider"
                                        title="Mute timer"
                                    >
                                        🔇
                                    </button>
                                )}
                                
                                <button
                                    onClick={() => onRemoveTimer(timer.id)}
                                    className="px-2 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-red-600 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider ml-auto"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))
                )}

                {/* Add Timer Form */}
                {showAddForm ? (
                    <div className="bg-neutral-50 hairline p-3 add-timer-form">
                        <div className="space-y-2">
                            <input
                                type="text"
                                placeholder="Timer name (e.g., 'Baking bread')"
                                value={newTimerTitle}
                                onChange={(e) => setNewTimerTitle(e.target.value)}
                                className="w-full px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-sans text-sm"
                                autoFocus
                            />
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="1"
                                    max="999"
                                    value={newTimerMinutes}
                                    onChange={(e) => setNewTimerMinutes(parseInt(e.target.value) || 1)}
                                    className="w-20 px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-mono text-sm"
                                />
                                <span className="font-mono text-sm text-neutral-900">minutes</span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleAddTimer}
                                    className="px-3 py-1 text-xs bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono uppercase tracking-wider"
                                >
                                    Add
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewTimerTitle('');
                                        setNewTimerMinutes(5);
                                    }}
                                    className="px-3 py-1 text-xs bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono uppercase tracking-wider"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="w-full px-3 py-2 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider"
                    >
                        + Add Timer
                    </button>
                )}
            </div>
        </div>
    );
};

export default TimerManagementPopup;

